import { apiService, isAxiosError } from "api/apiService";
import { toAxiosUploadProgress } from "shared/utils/axiosProgress";
import { getDateFromMonthYear } from "shared/utils/dateHelpers";
import { shouldFetch } from "shared/utils";
import useHelpers, { getCurrentSchoolYear } from "composables/useHelpers";
import { cloneDeep } from "lodash";
import { clusterManager } from "models/learning-objectives/clusterManager";
import { matrixLessonClass } from "models/learning-objectives/learningObjectivesMatrixModel";
import { LibraryCluster } from "models/learning-objectives/libraryClusterModel.js";
import paginationModel from "models/pagination.js";
import { defineStore } from "pinia";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { ref } from "vue";

/**
 * Learning Objectives Store
 *
 * This store manages the state and actions related to the learning objectives.
 */
export const useLearningObjectivesStore = defineStore(
  "learningObjectives",
  () => {
    const months = ref({});
    const weeks = ref([]);
    const schoolYears = ref([]);

    // Refs
    const libraryClusters = ref([]);
    const clusters = ref([]);
    const cluster = ref({});
    const libraryCluster = ref({});
    const myStudentsLearningObjectives = ref({});
    const myLessongroupsLearningObjectives = ref([]);
    const clustersByStudent = ref({});
    const library_clusters_pagination = ref({
      ...paginationModel,
      ...{ per_page: 9 },
    });
    const cluster_overview_filters = ref({});
    const matrix = ref([]);
    const roster = ref([]);
    const periods = ref([]);
    const library_learning_objectives_pagination = ref({});
    const libraryLearningObjectives = ref([]);
    const library_learning_objectives_filters = ref({});

    // Quasar
    const $q = useQuasar();

    // Helpers
    const { setPaginationMeta } = useHelpers();

    // States
    /**
     * State indicating if data is being fetched.
     * @type {import('vue').Ref<Object>}
     */
    const isFetching = ref({
      periods: false,
      clusters: false,
      library_clusters: false,
      library_cluster: false,
      my_lessongroups_learning_objectives: false,
      weeksBySchoolYear: false,
      cluster_overview_filters: false,
      matrix: false,
      roster: false,
      schoolYears: false,
      my_students_learning_objectives: {},
      clusters_by_student: {},
      library_learning_objectives: false,
      library_learning_objectives_filters: false,
    });

    /**
     * State indicating if data has been fetched.
     * @type {import('vue').Ref<Object>}
     */
    const hasFetched = ref({
      periods: false,
      clusters: false,
      library_clusters: false,
      library_cluster: false,
      my_lessongroups_learning_objectives: false,
      weeksBySchoolYear: false,
      cluster_overview_filters: false,
      matrix: false,
      roster: false,
      schoolYears: false,
      my_students_learning_objectives: {},
      clusters_by_student: {},
      library_learning_objectives: false,
      library_learning_objectives_filters: false,
    });

    const defaultLibraryCluster = {
      id: null,
      name: "",
      description: "",
      shortage: "",
      subjects: [],
      education_year_levels: [],
      editable: false,
      library_learning_objectives: [],
      attachments: [],
      links: [],
      videos: [],
      is_editing: false,
      meta: {
        can_update: true,
        can_delete: true,
      },
    };

    const defaultLearningObjective = {
      id: null,
      library_cluster_id: null,
      name: "",
      teacher_hints: "",
      attachments: [],
      links: [],
      videos: [],
      succes_criterias: [],
    };

    const defaultSuccessCriteria = {
      library_learning_objective_id: null,
      name: "",
      teacher_hints: "",
      order: 0,
      attachments: [],
      links: [],
      videos: [],
      is_editing: false,
    };

    const fetchSchoolYears = async ({ forceFetch = false } = {}) => {
      if (isFetching.value["schoolYears"]) {
        return isFetching.value["schoolYears"];
      }

      // Put this check in the manager
      if (!shouldFetch(hasFetched.value["schoolYears"]) && !forceFetch) {
        return schoolYears.value || [];
      }

      isFetching.value["schoolYears"] = (async () => {
        try {
          const {
            data: { data: fetchedSchoolYears },
          } = await apiService.get(`api/school/school-years`);

          hasFetched.value["schoolYears"] = new Date();

          schoolYears.value = fetchedSchoolYears;

          return fetchedSchoolYears;
        } catch (error) {
          console.log("🚀 ~ isFetching.value['schoolYears']= ~ error:", error);
        } finally {
          isFetching.value["schoolYears"] = false;
        }
      })();

      return isFetching.value["schoolYears"];
    };

    const setDefaultLibraryCluster = () => {
      libraryCluster.value = JSON.parse(JSON.stringify(defaultLibraryCluster));
    };

    /**
     * Fetches and sets the week numbers in a given school-period
     * it also sets the months
     * @returns {Array.<week>}
     */
    const fetchWeeksBySchoolYear = async ({
      forceFetch = false,
      schoolYear = getCurrentSchoolYear(),
    } = {}) => {
      if (isFetching.value["weeksBySchoolYear"]) {
        return isFetching.value["weeksBySchoolYear"];
      }

      if (!shouldFetch(hasFetched.value["weeksBySchoolYear"]) && !forceFetch) {
        return weeks.value || [];
      }

      isFetching.value["weeksBySchoolYear"] = (async () => {
        try {
          const schoolYearParameter = schoolYear.replace("/", "-");

          const {
            data: { data: fetchedWeeks },
          } = await apiService.get(
            `api/school-year-weeks/${schoolYearParameter}`,
          );

          hasFetched.value["weeksBySchoolYear"] = new Date();
          weeks.value = cloneDeep(fetchedWeeks);

          months.value = fetchedWeeks.reduce((acc, week) => {
            const month = getDateFromMonthYear(week.month, week.year);

            if (!acc[month]) {
              acc[month] = 0;
            }

            acc[month]++;

            return acc;
          }, {});

          return weeks.value;
        } catch (error) {
          console.log("🚀 ~ getSchoolYears ~ error:", error);
        } finally {
          isFetching.value["weeksBySchoolYear"] = false;
        }
      })();

      return isFetching.value["weeksBySchoolYear"];
    };

    const updatePeriod = async (period) => {
      const { schoolId } = useHelpers();

      try {
        const { data } = await apiService.put(
          `api/school/${schoolId}/periods/${period.id}`,
          period,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ updatePeriod ~ error:", error);
      }
    };

    const deletePeriod = async (periodId) => {
      const { schoolId } = useHelpers();

      try {
        const { data } = await apiService.delete(
          `api/school/${schoolId}/periods/${periodId}`,
        );

        console.log("🚀 ~ deletePeriod ~ data:", data);

        return data;
      } catch (error) {
        console.log("🚀 ~ updatePeriod ~ error:", error);
      }
    };

    /**
     * Creates a new period for the specified school.
     *
     * @param {Object} period - The period object containing the details of the period to be created.
     * @returns {Promise<void>} - A promise that resolves when the period is created.
     */
    const storePeriod = async (period) => {
      const { schoolId } = useHelpers();

      try {
        const { data } = await apiService.post(
          `api/school/${schoolId}/periods`,
          period,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ storePeriod ~ error:", error);
      }
    };

    /**
     * Fetches the periods for the specified school.
     * @returns {Promise<void>} - A promise that resolves when the periods are fetched.
     */
    const fetchPeriods = async ({
      forceFetch = false,
      schoolYear = getCurrentSchoolYear(),
    } = {}) => {
      const { schoolId } = useHelpers();

      if (isFetching.value["periods"]) {
        return isFetching.value["periods"];
      }

      // Put this check in the manager
      if (!shouldFetch(hasFetched.value["periods"]) && !forceFetch) {
        return periods.value || [];
      }

      isFetching.value["periods"] = (async () => {
        try {
          const schoolYearParameter = schoolYear.replace("/", "-");

          const {
            data: { data: fetchedPeriods },
          } = await apiService.get(`api/school/${schoolId}/periods`, {
            params: { school_year: schoolYearParameter },
          });

          hasFetched.value["periods"] = new Date();

          periods.value = fetchedPeriods;

          return fetchedPeriods;
        } catch (error) {
          console.log("🚀 ~ fetchPeriods ~ error:", error);
        } finally {
          isFetching.value["periods"] = false;
        }
      })();

      return isFetching.value["periods"];
    };

    const fetchRoster = async ({ forceFetch = false } = {}) => {
      const { schoolId } = useHelpers();

      if (isFetching.value["roster"]) {
        return isFetching.value["roster"];
      }

      // Put this check in the manager
      if (!shouldFetch(hasFetched.value["roster"]) && !forceFetch) {
        return roster.value || [];
      }

      isFetching.value["roster"] = (async () => {
        try {
          const { data: fetchedRoster } = await apiService.get(
            `api/school/${schoolId}/roster`,
          );

          hasFetched.value["roster"] = new Date();

          roster.value = fetchedRoster;

          return fetchedRoster;
        } catch (error) {
          console.log("🚀 ~ isFetching.value['roster']= ~ error:", error);
        } finally {
          isFetching.value["roster"] = false;
        }
      })();

      return isFetching.value["roster"];
    };

    const fetchClusters = async ({
      forceFetch = false,
      schoolYear = getCurrentSchoolYear(),
    } = {}) => {
      if (isFetching.value["clusters"]) {
        return isFetching.value["clusters"];
      }

      if (!shouldFetch(hasFetched.value["clusters"]) && !forceFetch) {
        return clusterManager.value.clusters || [];
      }

      isFetching.value["clusters"] = (async () => {
        const { schoolId } = useHelpers();

        try {
          const {
            data: { data: fetchedClusters },
          } = await apiService.get(`/api/school/${schoolId}/clusters`, {
            params: { school_year: schoolYear.replace("-", "/") },
          });

          hasFetched.value["clusters"] = new Date();

          clusters.value = fetchedClusters;

          return fetchedClusters;
        } catch (error) {
          console.log("🚀 ~ isFetching.value['clusters']= ~ error:", error);

          if (isAxiosError(error)) {
            return Promise.reject(error);
          }
        } finally {
          isFetching.value["clusters"] = false;
        }
      })();

      return isFetching.value["clusters"];
    };

    const fetchLibraryClusters = async ({
      forceFetch = false,
      filters = {},
      excludeTrashed = false,
    } = {}) => {
      if (isFetching.value["library_clusters"]) {
        return isFetching.value["library_clusters"];
      }

      if (!shouldFetch(hasFetched.value["library_clusters"]) && !forceFetch) {
        return libraryClusters.value || [];
      }

      isFetching.value["library_clusters"] = (async () => {
        const { schoolId } = useHelpers();

        // Only use the needed values from the pagination
        const { page, per_page } = library_clusters_pagination.value;

        try {
          const {
            data: { data: fetchedClusters, meta },
          } = await apiService.get(`/api/school/${schoolId}/library-clusters`, {
            params: {
              ...{ page, per_page },
              ...(Object.keys(filters).length && filters),
              ...(excludeTrashed && { exclude_trashed: excludeTrashed }),
            },
          });

          // Set the current fetched pagination information

          Object.assign(
            library_clusters_pagination.value,
            setPaginationMeta(meta),
          );

          hasFetched.value["library_clusters"] = new Date();

          libraryClusters.value = fetchedClusters;

          fetchedClusters.forEach((cluster, key) => {
            libraryClusters.value[key] = new LibraryCluster(cluster);
          });

          return libraryClusters.value;
        } catch (error) {
          console.log(
            "🚀 ~ isFetching.value['library_clusters']= ~ error:",
            error,
          );

          if (isAxiosError(error)) {
            return Promise.reject(error);
          }
        } finally {
          isFetching.value["library_clusters"] = false;
        }
      })();

      return isFetching.value["library_clusters"];
    };

    const fetchLibraryLearningObjectives = async ({
      forceFetch = false,
      filters = {},
      excludeTrashed = false,
    } = {}) => {
      if (isFetching.value["library_learning_objectives"] && !forceFetch) {
        return isFetching.value["library_learning_objectives"];
      }

      if (
        !shouldFetch(hasFetched.value["library_learning_objectives"]) &&
        !forceFetch
      ) {
        return libraryLearningObjectives.value || [];
      }

      isFetching.value["library_learning_objectives"] = (async () => {
        const { schoolId } = useHelpers();

        // Only use the needed values from the pagination
        const { page, per_page } = library_learning_objectives_pagination.value;

        try {
          const {
            data: { data: fetchedClusters, meta },
          } = await apiService.get(
            `/api/school/${schoolId}/library-learning-objectives`,
            {
              params: {
                ...{ page, per_page },
                ...(Object.keys(filters).length && filters),
                ...(excludeTrashed && { exclude_trashed: excludeTrashed }),
              },
            },
          );

          // Set the current fetched pagination information
          Object.assign(
            library_learning_objectives_pagination.value,
            setPaginationMeta(meta),
          );

          hasFetched.value["library_learning_objectives"] = new Date();

          libraryLearningObjectives.value = fetchedClusters;

          return libraryLearningObjectives.value;
        } catch (error) {
          throw new Error(error);
        } finally {
          isFetching.value["library_learning_objectives"] = false;
        }
      })();

      return isFetching.value["library_learning_objectives"];
    };

    /**
     * Fetches a specific cluster by its ID.
     * @param {Object} options - The options for fetching the cluster.
     * @param {string|null} [options.id=null] - The ID of the cluster to fetch.
     * @returns {Promise<Object>} The fetched cluster.
     */
    const fetchLibraryCluster = async ({ id = null } = {}) => {
      const { schoolId } = useHelpers();
      if (!id) {
        return;
      }

      try {
        const { data: fetchedCluster } = await apiService.get(
          `/api/school/${schoolId}/library-clusters/${id}`,
        );

        return new LibraryCluster(fetchedCluster.data);
      } catch (error) {
        throw new Error(error);
      }
    };

    /**
     * Stores the current cluster to the server.
     * @returns {Promise<Object>} The stored cluster data.
     */
    const storeCluster = async (cluster, { onUploadProgress } = {}) => {
      const { schoolId } = useHelpers();
      const { rewriteModelToFormData } = useHelpers();

      const formData = rewriteModelToFormData(cluster);

      try {
        const { data } = await apiService.post(
          `/api/school/${schoolId}/clusters`,
          formData,
          { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ storeCluster ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    /**
     * Stores the current cluster to the server.
     * @returns {Promise<Object>} The stored cluster data.
     */
    const storeClusterFromLibrary = async (
      cluster,
      { onUploadProgress } = {},
    ) => {
      const { schoolId } = useHelpers();

      try {
        const { data } = await apiService.post(
          `/api/school/${schoolId}/clusters/from-library`,
          cluster,
          { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ storeClusterFromLibrary ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    /**
     * Updates a specific cluster by its ID.
     *
     * @param {string|null} [options.id=null] - The ID of the cluster to update.
     * @returns {Promise<Object>} The updated cluster.
     * @param cluster
     */
    const updateCluster = async (cluster, { onUploadProgress } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      try {
        const { id } = cluster;

        const formData = rewriteModelToFormData(cluster);
        formData.append("_method", "PUT");

        const { data } = await apiService.post(
          `/api/school/${schoolId}/clusters/${id}`,
          formData,
          { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ updateCluster ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    /**
     * Updates a specific library cluster by its ID.
     *
     * @param {Object} options - The options for updating the cluster.
     * @param {string|null} [options.id=null] - The ID of the cluster to update.
     * @returns {Promise<Object>} The updated cluster.
     */
    const updateLibraryCluster = async (cluster, { onUploadProgress } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      const { id: currentClusterId } = cluster;

      const formData = rewriteModelToFormData(cluster);
      formData.append("_method", "PUT");

      const {
        data: { data: updatedCluster },
      } = await apiService.post(
        `/api/school/${schoolId}/library-clusters/${currentClusterId}`,
        formData,
        { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
      );

      const updatedLibraryCluster = Object.assign(
        libraryCluster.value,
        updatedCluster,
      );

      const foundCluster = libraryClusters.value.find(
        ({ id }) => id === currentClusterId,
      );

      if (foundCluster) {
        Object.assign(foundCluster, updatedLibraryCluster);
      }

      return updatedLibraryCluster;
    };

    /**
     *     * Adds a learning objective to a specific library cluster.
     *      *
     *      * @param {Object} options - The options for adding the learning objective.
     *      * @param {Object} [options.learningObjective={}] - The learning objective to add.
     *      * @param {string|null} [options.clusterId=null] - The ID of the cluster to add the learning objective to.
     *      * @returns {Promise<Object>} The added learning objective.
     *      */
    const addLearningObjectiveToLibraryCluster = async ({
      learningObjective = {},
      clusterId = null,
      onUploadProgress = undefined,
    } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();
      if (!clusterId || !schoolId) {
        return;
      }

      try {
        const formData = rewriteModelToFormData(learningObjective);

        const { data: response } = await apiService.post(
          `/api/school/${schoolId}/library-clusters/${clusterId}/library-learning-objectives`,
          formData,
          { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
        );

        Object.assign(libraryCluster.value.learning_objectives, response.data);

        $q.notify({
          message: "Leerdoel toegevoegd",
          color: "positive",
        });

        return response.data;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    /**
     * Adds success criteria to a specific library learning objective.
     *
     * @param {Object} options - The options for adding the success criteria.
     * @param {Object} [options.successCriteria={}] - The success criteria to add.
     * @param {string|null} [options.learningObjectiveId=null] - The ID of the library learning objective to add the success criteria to.
     * @returns {Promise<Object>} The added success criteria.
     */
    const addSuccessCriteriaToLibraryLearningObjective = async ({
      successCriteria = {},
      learningObjectiveId = null,
      clusterId = null,
    } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      if (!learningObjectiveId || !clusterId || !schoolId) {
        return;
      }

      try {
        const formData = rewriteModelToFormData(successCriteria);

        const { data: response } = await apiService.post(
          `/api/school/${schoolId}/library-clusters/${clusterId}/library-learning-objectives/${learningObjectiveId}/library-success-criteria`,
          formData,
        );

        return response.data;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    /**
     * Deletes a success criteria from a specific learning objective.
     *
     * @param {Object} options - The options for deleting the success criteria.
     * @param {string|null} [options.learningObjectiveId=null] - The ID of the learning objective.
     * @param {string|null} [options.successCriteriaId=null] - The ID of the success criteria.
     * @param {boolean} [options.library=false] - Whether the learning objective is from the library.
     * @returns {Promise<void>} - A promise that resolves when the success criteria is deleted.
     */
    const deleteSuccessCriteriaFromLibraryLearningObjective = async ({
      learningObjectiveId = null,
      successCriteriaId = null,
      clusterId = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (
        !learningObjectiveId ||
        !schoolId ||
        !successCriteriaId ||
        !clusterId
      ) {
        return Promise.reject("Invalid data");
      }

      try {
        await apiService.delete(
          `/api/school/${schoolId}/library-clusters/${clusterId}/library-learning-objectives/${learningObjectiveId}/library-success-criteria/${successCriteriaId}`,
        );

        const foundLibCluster = libraryClusters.value.find(
          ({ id }) => id === clusterId,
        );

        if (!foundLibCluster) {
          $q.notify({
            message:
              "Succes criteria is wel verwijderd maar kan nog steeds zichtbaar zijn, hier is wat mis gegaan. Refresh je pagina",
            color: "negative",
          });

          return;
        }

        const foundLearningObjective = foundLibCluster.learning_objectives.find(
          ({ id }) => id === learningObjectiveId,
        );

        if (!foundLearningObjective) {
          $q.notify({
            message:
              "Succes criteria is wel verwijderd maar kan nog steeds zichtbaar zijn, hier is wat mis gegaan. Refresh je pagina",
            color: "negative",
          });

          return;
        }

        const clusterIndex = foundLearningObjective.success_criterias.findIndex(
          ({ id }) => id === successCriteriaId,
        );

        foundLearningObjective.success_criterias.splice(clusterIndex, 1);

        if (libraryCluster.value.id === clusterId) {
          const _foundLearningObjective =
            libraryCluster.value.learning_objectives.find(
              ({ id }) => id === learningObjectiveId,
            );

          if (!foundLearningObjective) {
            return;
          }

          const _clusterIndex =
            _foundLearningObjective.success_criterias.findIndex(
              ({ id }) => id === successCriteriaId,
            );

          _foundLearningObjective.success_criterias.splice(_clusterIndex, 1);
        }

        $q.notify({
          message: "Succes criteria verwijderd",
          color: "positive",
        });
      } catch (error) {
        console.log(error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    /**
     * Deletes a learning objective from a specific cluster.
     *
     * @param {Object} options - The options for deleting the learning objective.
     * @param {string|null} [options.learningObjectiveId=null] - The ID of the learning objective to delete.
     * @param {string|null} [options.clusterId=null] - The ID of the cluster from which to delete the learning objective.
     * @param {boolean} [options.library=false] - Whether the learning objective is from the library.
     * @returns {Promise<void>} - A promise that resolves when the learning objective is deleted.
     */
    const deleteLearningObjectiveFromLibraryCluster = async ({
      learningObjectiveId = null,
      clusterId = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (!learningObjectiveId || !schoolId || !clusterId) {
        return Promise.reject("Invalid data");
      }

      try {
        $q.notify({
          message: "Leerdoel verwijderd van cluster",
          color: "positive",
        });

        return await apiService.delete(
          `api/school/${schoolId}/library-clusters/${clusterId}/library-learning-objectives/${learningObjectiveId}`,
        );
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    /**
     * Deletes a specific learning objective cluster.
     *
     * @param {Object} options - The options for deleting the learning objective cluster.
     * @param {string|null} [options.clusterId=null] - The ID of the cluster to delete.
     * @param {boolean} [options.library=false] - Whether the cluster is from the library.
     * @returns {Promise<void>} - A promise that resolves when the cluster is deleted.
     */
    const deleteLibraryCluster = async ({ clusterId = null } = {}) => {
      const { schoolId } = useHelpers();

      if (!clusterId || !schoolId) {
        return Promise.reject("Invalid data");
      }

      try {
        const response = await apiService.delete(
          `/api/school/${schoolId}/library-clusters/${clusterId}`,
        );

        libraryClusters.value = libraryClusters.value.filter(
          (cluster) => cluster.id !== clusterId,
        );

        return response;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    const deleteCluster = async (clusterId) => {
      const { schoolId } = useHelpers();

      try {
        const { data } = await apiService.delete(
          `api/school/${schoolId}/clusters/${clusterId}`,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ deleteCluster ~ error:", error);
      }
    };

    /**
     * Fetches the learning objectives for the current user's students.
     *
     * @param {Object} options - The options for fetching the learning objectives.
     * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
     * @param {string|null} [options.teacherId=null] - The ID of the teacher.
     * @returns {Promise<Array<Object>>} The fetched learning objectives.
     */
    const fetchMyStudentsLearningObjectives = async ({
      forceFetch = false,
      periodId = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (periodId == null) {
        throw new Error("Period ID is required to fetch clusters");
      }

      if (!schoolId) {
        return;
      }

      if (isFetching.value["my_students_learning_objectives"][periodId]) {
        return;
      }

      if (
        !shouldFetch(
          hasFetched.value["my_students_learning_objectives"][periodId],
        ) &&
        !forceFetch
      ) {
        return myStudentsLearningObjectives.value?.[periodId] || [];
      }

      try {
        isFetching.value["my_students_learning_objectives"][periodId] = true;

        const { data: fetchedLearningObjectives } = await apiService.get(
          `/api/teacher/my-students/learning-objectives`,
          {
            params: { "period-id": periodId },
          },
        );

        hasFetched.value["my_students_learning_objectives"][periodId] =
          new Date();

        myStudentsLearningObjectives.value[periodId] =
          fetchedLearningObjectives.data;

        return myStudentsLearningObjectives.value[periodId];
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      } finally {
        isFetching.value["my_students_learning_objectives"][periodId] = false;
      }
    };

    const fetchMyStudentsLearningObjectivesSingleStudent = async ({
      forceFetch = false,
      args = {},
    } = {}) => {
      const {
        studentId = null,
        scheduleId = null,
        periodId = null,
      } = args || {};

      if (studentId == null || scheduleId == null || periodId == null) {
        throw new Error(
          "Missing required arguments in fetchMyStudentsLearningObjectivesSingleStudent",
        );
      }

      const foundSubject = myStudentsLearningObjectives.value[periodId]
        .find((student) => student.user.id === studentId)
        ?.subjects.find((subject) => subject.id === scheduleId);

      if (!foundSubject) {
        return;
      }

      if (foundSubject.clusters !== undefined && !forceFetch) {
        return;
      }

      try {
        const { data: fetchedClusters } = await apiService.get(
          `/api/teacher/my-students/learning-objectives/${studentId}/${scheduleId}`,
        );

        foundSubject.clusters = fetchedClusters;

        return fetchedClusters;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    /**
     * Fetches the learning objectives for the current user's lesson groups.
     *
     * @param {Object} options - The options for fetching the learning objectives.
     * @param {boolean} [options.forceFetch=false] - Whether to force fetch the data.
     * @param {string|null} [options.teacherId=null] - The ID of the teacher.
     * @returns {Promise<Array<Object>>} The fetched learning objectives.
     */
    const fetchMyLessongroupsLearningObjectives = async ({
      forceFetch = false,
      teacherId = null,
      periodId = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (!schoolId || !teacherId) {
        return;
      }

      if (isFetching.value["my_lessongroups_learning_objectives"]) {
        return;
      }

      if (
        !shouldFetch(hasFetched.value["my_lessongroups_learning_objectives"]) &&
        !forceFetch
      ) {
        return myStudentsLearningObjectives.value || [];
      }

      try {
        isFetching.value["my_lessongroups_learning_objectives"] = true;

        const { data: fetchedLearningObjectives } = await apiService.get(
          `/api/teacher/${teacherId}/lesson-groups/learning-objectives`,
          {
            params: { "period-id": periodId },
          },
        );

        hasFetched.value["my_lessongroups_learning_objectives"] = new Date();

        myLessongroupsLearningObjectives.value = fetchedLearningObjectives.data;

        return fetchedLearningObjectives.data;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);

          return Promise.reject(error);
        }
      } finally {
        isFetching.value["my_lessongroups_learning_objectives"] = false;
      }
    };

    const fetchClustersByStudent = async ({
      forceFetch = false,
      periodId = null,
    } = {}) => {
      if (periodId == null) {
        throw new Error("Period ID is required to fetch clusters");
      }

      if (isFetching.value["clusters_by_student"][periodId]) {
        return;
      }

      if (
        !shouldFetch(hasFetched.value["clusters_by_student"][periodId]) &&
        !forceFetch
      ) {
        return clustersByStudent.value?.[periodId] || [];
      }

      try {
        isFetching.value["clusters_by_student"][periodId] = true;

        const { data: fetchedClusters } = await apiService.get(
          `/api/my-learning-objectives`,
          {
            params: { "period-id": periodId },
          },
        );

        hasFetched.value["clusters_by_student"][periodId] = new Date();

        clustersByStudent.value[periodId] = fetchedClusters;

        return clustersByStudent.value[periodId];
      } catch (error) {
        console.log("🚀 ~ error:", error);
        throw new Error(error);
      } finally {
        isFetching.value["clusters_by_student"][periodId] = false;
      }
    };

    const findSubjectById = (data, subjectId) => {
      for (const [, { subjects }] of Object.entries(data)) {
        const subject = subjects.find(({ id }) => id === subjectId);

        if (subject) {
          return subject;
        }
      }

      return null;
    };

    const addLearningObjectiveRating = async ({
      learningObjectiveId = null,
      rating = null,
      periodId = null,
    } = {}) => {
      try {
        const { data } = await apiService.put(
          `/api/my-learning-objectives/rate-learning-objective/${learningObjectiveId}`,
          {
            rating,
            ...(periodId && { "period-id": periodId }),
          },
        );

        const foundSubject = clustersByStudent.value[periodId].subjects.find(
          ({ id }) => id === parseInt(data.id),
        );

        if (foundSubject == null) {
          throw new Error("Subject not found");
        }

        Object.assign(foundSubject, data);

        $q.notify({
          message: "Leerdoel beoordeeld",
          color: "positive",
        });

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);
        if (isAxiosError(error)) {
          console.log(error);
          $q.notify({
            message: `Er is iets fout gegaan bij het beoordelen van het leerdoel ${error?.response?.data?.message ?? ""}`,
            color: "negative",
          });

          return Promise.reject(error);
        }
      }
    };

    const addSuccessCriteriaRating = async ({
      successCriteriaId = null,
      rating = null,
      periodId = null,
    } = {}) => {
      try {
        const { data } = await apiService.put(
          `/api/my-learning-objectives/rate-success-criteria/${successCriteriaId}`,
          {
            rating,
            ...(periodId && { "period-id": periodId }),
          },
        );

        const foundSubject = clustersByStudent.value[periodId].subjects.find(
          ({ id }) => id === parseInt(data.id),
        );

        if (foundSubject == null) {
          throw new Error("Subject not found");
        }

        Object.assign(foundSubject, data);

        $q.notify({
          message: "Succes criteria beoordeeld",
          color: "positive",
        });

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        $q.notify({
          message:
            "Er is iets fout gegaan bij het beoordelen van het succes criteria",
          color: "negative",
        });

        if (error instanceof Error) {
          throw error;
        }

        throw new Error(error);
      }
    };

    const fetchClusterOverviewFilters = async ({ forceFetch = false } = {}) => {
      if (
        !shouldFetch(hasFetched.value["cluster_overview_filters"]) &&
        !forceFetch
      ) {
        return cluster_overview_filters.value || [];
      }

      const { schoolId } = useHelpers();

      isFetching.value["cluster_overview_filters"] = true;

      try {
        const { data: fetchedData } = await apiService.get(
          `/api/school/${schoolId}/library-clusters/filters`,
        );

        hasFetched.value["learning_objectives_filters"] = new Date();

        cluster_overview_filters.value = fetchedData.data;

        return cluster_overview_filters.value;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);

          return Promise.reject(error);
        }
      } finally {
        isFetching.value["cluster_overview_filters"] = false;
      }
    };

    const updateLearningObjective = async (learningObjective) => {
      try {
        const { schoolId, rewriteModelToFormData } = useHelpers();
        const { id, cluster_id } = learningObjective;

        const formData = rewriteModelToFormData(learningObjective);
        formData.append("_method", "PUT");

        const { data } = await apiService.post(
          `api/school/${schoolId}/clusters/${cluster_id}/learning-objectives/${id}`,
          formData,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ updateLearningObjective ~ error:", error);

        return error;
      }
    };

    const fetchLibraryLearningObjectivesFilters = async ({
      forceFetch = false,
    } = {}) => {
      if (
        !shouldFetch(hasFetched.value["library_learning_objectives_filters"]) &&
        !forceFetch
      ) {
        return library_learning_objectives_filters.value || [];
      }

      const { schoolId } = useHelpers();

      isFetching.value["library_learning_objectives_filters"] = true;

      try {
        const { data: fetchedData } = await apiService.get(
          `/api/school/${schoolId}/library-learning-objectives/filters`,
        );

        hasFetched.value["library_learning_objectives_filters"] = new Date();

        library_learning_objectives_filters.value = fetchedData.data;

        return library_learning_objectives_filters.value;
      } catch (error) {
        if (isAxiosError(error)) {
          throw new Error(error);
        }
      } finally {
        isFetching.value["library_learning_objectives_filters"] = false;
      }
    };

    const updateLibraryLearningObjective = async (learningObjective) => {
      try {
        const { schoolId, rewriteModelToFormData } = useHelpers();
        const { id, library_cluster_id } = learningObjective;

        const formData = rewriteModelToFormData(learningObjective);
        formData.append("_method", "PUT");

        const { data } = await apiService.post(
          `api/school/${schoolId}/library-clusters/${library_cluster_id}/library-learning-objectives/${id}`,
          formData,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ updateLearningObjective ~ error:", error);

        return error;
      }
    };

    const deleteLearningObjective = async (learningObjective) => {
      try {
        const { schoolId } = useHelpers();
        const { id, cluster_id } = learningObjective;

        await apiService.delete(
          `api/school/${schoolId}/clusters/${cluster_id}/learning-objectives/${id}`,
        );
      } catch (error) {
        console.log("🚀 ~ updateLearningObjective ~ error:", error);

        return error;
      }
    };

    const addLearningObjective = async ({ learningObjective, clusterId }) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      const formData = rewriteModelToFormData(learningObjective);

      const { data } = await apiService.post(
        `api/school/${schoolId}/clusters/${clusterId}/learning-objectives`,
        formData,
      );

      return data;
    };

    const addSuccessCriteriaToLearningObjective = async ({
      learningObjectiveId,
      clusterId,
      successCriteria,
    }) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      if (!learningObjectiveId || !clusterId || !schoolId) {
        throw new Error("Invalid data");
      }

      try {
        // Remove the library_learning_objective_id from the success criteria
        // eslint-disable-next-line no-unused-vars
        const { library_learning_objective_id, ...rest } = successCriteria;

        const formData = rewriteModelToFormData(rest);

        const { data } = await apiService.post(
          `api/school/${schoolId}/clusters/${clusterId}/learning-objectives/${learningObjectiveId}/success-criteria`,
          formData,
        );

        return data;
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
          return Promise.reject(error);
        }
      }
    };

    const removeSuccessCriteriaFromLearningObjective = async ({
      learningObjectiveId = null,
      successCriteriaId = null,
      clusterId = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (
        !learningObjectiveId ||
        !successCriteriaId ||
        !clusterId ||
        !schoolId
      ) {
        throw new Error("Invalid data");
      }

      try {
        await apiService.delete(
          `api/school/${schoolId}/clusters/${clusterId}/learning-objectives/${learningObjectiveId}/success-criteria/${successCriteriaId}`,
        );
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const addChatToCluster = async (cluster) => {
      try {
        const { data } = await apiService.post(
          `/api/cluster-students/${cluster.id}/chat`,
          cluster,
        );

        cluster.chats = cluster.chats ?? [];
        cluster.chats.push(data);
      } catch (error) {
        console.log("🚀 ~ addChatToCluster ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const updateLearningObjectiveOrder = async ({
      learningObjectiveId = null,
      clusterId = null,
      order = null,
      type = "cluster",
    } = {}) => {
      const { schoolId } = useHelpers();

      if (!learningObjectiveId || !clusterId || !schoolId) {
        throw new Error("Invalid data");
      }

      const isLibraryCluster = type === "library_cluster" ? "library-" : "";

      try {
        // Store the changed order
        const { data } = await apiService.post(
          `api/school/${schoolId}/${isLibraryCluster}clusters/${clusterId}/${isLibraryCluster}learning-objectives/${learningObjectiveId}/move`,
          { order },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const updateSuccessCriteriaOrder = async ({
      clusterId = null,
      learningObjectiveId = null,
      successCriteriaId = null,
      order = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (
        !learningObjectiveId ||
        !successCriteriaId ||
        !schoolId ||
        !clusterId
      ) {
        throw new Error("Invalid data in updateSuccessCriteriaOrder");
      }

      try {
        // Store the changed order
        const { data } = await apiService.post(
          `api/school/${schoolId}/clusters/${clusterId}/learning-objectives/${learningObjectiveId}/success-criteria/${successCriteriaId}/move`,
          { order },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const updateLibrarySuccessCriteriaOrder = async ({
      libraryLearningObjectiveId = null,
      librarySuccessCriteriaId = null,
      order = null,
    } = {}) => {
      const { schoolId } = useHelpers();

      if (
        !libraryLearningObjectiveId ||
        !librarySuccessCriteriaId ||
        !schoolId
      ) {
        throw new Error("Invalid data");
      }

      try {
        // Store the changed order
        const { data } = await apiService.post(
          `api/school/${schoolId}/library-learning-objectives/${libraryLearningObjectiveId}/library-success-criteria/${librarySuccessCriteriaId}/move`,
          { order },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const updateSuccessCriteria = async ({
      learningObjectiveId = null,
      clusterId = null,
      successCriteria = null,
    } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      if (
        !learningObjectiveId ||
        !clusterId ||
        !successCriteria.id ||
        !schoolId
      ) {
        throw new Error("Invalid data");
      }

      try {
        const formData = rewriteModelToFormData(successCriteria);
        formData.append("_method", "PUT");

        // Store the changed succcess criteria
        const { data } = await apiService.post(
          `api/school/${schoolId}/clusters/${clusterId}/learning-objectives/${learningObjectiveId}/success-criteria/${successCriteria.id}`,
          formData,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const updateLibrarySuccessCriteria = async ({
      libraryLearningObjectiveId = null,
      libraryClusterId = null,
      successCriteria = null,
    } = {}) => {
      const { schoolId, rewriteModelToFormData } = useHelpers();

      if (
        !libraryLearningObjectiveId ||
        !libraryClusterId ||
        !successCriteria.id ||
        !schoolId
      ) {
        throw new Error("Invalid data");
      }

      try {
        const formData = rewriteModelToFormData(successCriteria);
        formData.append("_method", "PUT");

        // Store the changed succcess criteria
        const { data } = await apiService.post(
          `api/school/${schoolId}/library-clusters/${libraryClusterId}/library-learning-objectives/${libraryLearningObjectiveId}/library-success-criteria/${successCriteria.id}`,
          formData,
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const setClusterChatsAsRead = async ({ clusterId, lastChatId } = {}) => {
      if (!clusterId || !lastChatId) {
        console.error("No cluster ID or last chat ID provided");
        return Promise.reject("No cluster ID or last chat ID provided");
      }

      try {
        const { data } = await apiService.post(
          `/api/cluster-students/${clusterId}/chat/mark-read`,
          {
            chat_id: lastChatId,
          },
        );

        return data;
      } catch (error) {
        console.log("🚀 ~ error:", error);

        if (isAxiosError(error)) {
          return Promise.reject(error);
        }
      }
    };

    const fetchLearningObjectivesMatix = async ({
      forceFetch = false,
      periodId = null,
    } = {}) => {
      const { employeeId } = useHelpers();

      if (isFetching.value["matrix"] && !forceFetch) {
        return isFetching.value["matrix"];
      }

      // Put this check in the manager
      if (!shouldFetch(hasFetched.value["matrix"]) && !forceFetch) {
        return matrix.value || [];
      }

      isFetching.value["matrix"] = (async () => {
        try {
          const {
            data: { data: lessonClasses },
          } = await apiService.get(
            `api/teacher/${employeeId}/lesson-groups/learning-objectives/cross-table`,
            {
              params: { "period-id": periodId },
            },
          );

          hasFetched.value["matrix"] = new Date();

          // Clear the matrix value before pushing new data
          matrix.value = [];

          lessonClasses.forEach((lessonClass) => {
            matrix.value.push(new matrixLessonClass(lessonClass));
          });

          return matrix.value;
        } catch (error) {
          console.log("🚀 ~ isFetching.value['matrix']= ~ error:", error);
        } finally {
          isFetching.value["matrix"] = false;
        }
      })();

      return isFetching.value["matrix"];
    };

    const postLearningObjectivesToModel = async (
      addedLearningObjectives,
      cluster,
    ) => {
      const { id } = cluster;

      const { schoolId } = useHelpers();

      // Check if the cluster is a library cluster
      const isLibraryCluster =
        cluster.type === "library_cluster" ? "library-" : "";

      // Existing cluster so make a call to add the learning objectives
      const addedLearningObjectivesIds = addedLearningObjectives.map(
        ({ id }) => id,
      );

      // Also post the added learning objectives to the cluster
      const existingLearningObjectivesIds = cluster.learning_objectives.map(
        ({ library_learning_objective_id = null, id = null }) =>
          library_learning_objective_id ?? id,
      );

      const allLearningObjectivesIds = [
        ...existingLearningObjectivesIds,
        ...addedLearningObjectivesIds,
      ];

      // Let a failure propagate: the callers (useClusterAdd.commit and
      // ClusterForm) already show the error toast, so notifying here too would
      // double-toast the live edit-cluster path.
      const { data } = await apiService.post(
        `/api/school/${schoolId}/${isLibraryCluster}clusters/${id}/add-library-learning-objectives`,
        {
          library_learning_objective_ids: allLearningObjectivesIds,
        },
      );

      // Wrap the raw API objectives into the JS cluster-learning-objective
      // models (client_key etc.) so the modal renders them without a reload.
      cluster.setClusterLearningObjectives(data);

      $q.notify({
        type: "positive",
        message: "Leerdoelen toegevoegd aan cluster",
      });

      return data;
    };

    return {
      isFetching,
      months,
      weeks,
      schoolYears,
      fetchSchoolYears,
      libraryClusters,
      cluster,
      matrix,
      libraryCluster,
      defaultLearningObjective,
      defaultSuccessCriteria,
      myStudentsLearningObjectives,
      myLessongroupsLearningObjectives,
      clustersByStudent,
      library_clusters_pagination,
      cluster_overview_filters,
      libraryLearningObjectives,
      library_learning_objectives_pagination,
      library_learning_objectives_filters,
      storePeriod,
      fetchPeriods,
      fetchRoster,
      roster,
      fetchWeeksBySchoolYear,
      fetchClusters,
      fetchLibraryClusters,
      fetchLibraryCluster,
      setDefaultLibraryCluster,
      storeCluster,
      storeClusterFromLibrary,
      updateCluster,
      updateLibraryCluster,
      updatePeriod,
      deletePeriod,
      addLearningObjectiveToLibraryCluster,
      addSuccessCriteriaToLibraryLearningObjective,
      deleteSuccessCriteriaFromLibraryLearningObjective,
      deleteLearningObjectiveFromLibraryCluster,
      deleteLibraryCluster,
      deleteCluster,
      fetchMyStudentsLearningObjectives,
      fetchMyLessongroupsLearningObjectives,
      fetchClustersByStudent,
      addLearningObjectiveRating,
      addSuccessCriteriaRating,
      fetchMyStudentsLearningObjectivesSingleStudent,
      fetchClusterOverviewFilters,
      updateLearningObjective,
      updateLibraryLearningObjective,
      deleteLearningObjective,
      addLearningObjective,
      addSuccessCriteriaToLearningObjective,
      removeSuccessCriteriaFromLearningObjective,
      addChatToCluster,
      updateLearningObjectiveOrder,
      updateSuccessCriteriaOrder,
      updateLibrarySuccessCriteriaOrder,
      updateSuccessCriteria,
      updateLibrarySuccessCriteria,
      setClusterChatsAsRead,
      fetchLearningObjectivesMatix,
      findSubjectById,
      periods,
      fetchLibraryLearningObjectives,
      fetchLibraryLearningObjectivesFilters,
      postLearningObjectivesToModel,
    };
  },
);

// if (import.meta.hot) {
//   import.meta.hot.accept(
//     acceptHMRUpdate(useLearningObjectivesStore, import.meta.hot),
//   );
// }
